import api from '../axiosConfig';
import { EXECUTIVE_DASHBOARD, WEEKLY_CHART, REVENUE_BREAKDOWN, EXPENSE_BREAKDOWN, ACTIVITY_LOG, DASHBOARD_ALERTS, DISMISS_ALERT, RESOLVE_ALERT, DASHBOARD_PREFERENCES } from '../config/apiEndpoints';

/**
 * Service API for Executive Dashboard operations.
 */
export const DashboardService = {
    /**
     * Fetches the latest administrative dashboard summary.
     * @param {string} tenantId - The active tenant identifier.
     * @returns {Promise<any>}
     */
    fetchStats: async (tenantId) => {
        try {
            const response = await api.get(EXECUTIVE_DASHBOARD, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('DashboardService fetchStats error:', error);
            throw error;
        }
    },

    /**
     * Fetches the weekly performance chart data.
     * @param {string} tenantId - The active tenant identifier.
     * @param {Object} [params] - Optional parameters.
     * @param {string} [params.period] - The period for aggregation: 'this_week', 'last_week', or 'this_month'.
     * @returns {Promise<any>}
     */
    fetchWeeklyChart: async (tenantId, params = {}) => {
        try {
            const response = await api.get(WEEKLY_CHART, {
                headers: { 'x-tenant-id': tenantId },
                params
            });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('DashboardService fetchWeeklyChart error:', error);
            throw error;
        }
    },

    /**
     * Fetches the revenue breakdown chart data.
     * @param {string} tenantId - The active tenant identifier.
     * @param {Object} [params] - Optional parameters (period, startDate, endDate).
     * @returns {Promise<any>}
     */
    fetchRevenueBreakdown: async (tenantId, params = {}) => {
        try {
            const response = await api.get(REVENUE_BREAKDOWN, {
                headers: { 'x-tenant-id': tenantId },
                params
            });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('DashboardService fetchRevenueBreakdown error:', error);
            throw error;
        }
    },

    /**
     * Fetches the expense breakdown chart data.
     * @param {string} tenantId - The active tenant identifier.
     * @param {Object} [params] - Optional parameters (period, startDate, endDate).
     * @returns {Promise<any>}
     */
    fetchExpenseBreakdown: async (tenantId, params = {}) => {
        try {
            const response = await api.get(EXPENSE_BREAKDOWN, {
                headers: { 'x-tenant-id': tenantId },
                params
            });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('DashboardService fetchExpenseBreakdown error:', error);
            throw error;
        }
    },

    /**
     * Fetches the recent system activity logs.
     * @param {string} tenantId - The active tenant identifier.
     * @returns {Promise<any>}
     */
    fetchActivityLog: async (tenantId) => {
        try {
            const response = await api.get(ACTIVITY_LOG, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('DashboardService fetchActivityLog error:', error);
            throw error;
        }
    },

    /**
     * Fetches system alerts based on status filters.
     * @param {string} tenantId - The active tenant identifier.
     * @param {Object} [params] - Optional parameters.
     * @param {string} [params.status] - Filter by alert status: 'Active', 'Dismissed', or 'Resolved'.
     * @returns {Promise<any>}
     */
    fetchAlerts: async (tenantId, params = {}) => {
        try {
            const response = await api.get(DASHBOARD_ALERTS, {
                headers: { 'x-tenant-id': tenantId },
                params
            });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('DashboardService fetchAlerts error:', error);
            throw error;
        }
    },

    /**
     * Updates an alert status (Resolve / Dismiss).
     * @param {string} tenantId - The active tenant identifier.
     * @param {string|number} alertId - The target alert identifier.
     * @param {string} status - New alert status: 'Resolved' or 'Dismissed'.
     * @returns {Promise<any>}
     */
    updateAlertStatus: async (tenantId, alertId, status) => {
        try {
            const response = await api.patch(`${DASHBOARD_ALERTS}/${alertId}`, { status }, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('DashboardService updateAlertStatus error:', error);
            throw error;
        }
    },

    /**
     * Manually creates/broadcasts a system alert.
     * @param {string} tenantId - The active tenant identifier.
     * @param {Object} payload - The alert payload.
     * @param {string} payload.type - The alert type: 'Critical', 'Warning', 'Info'.
     * @param {string} payload.message - The alert message.
     * @returns {Promise<any>}
     */
    createAlert: async (tenantId, payload) => {
        try {
            const response = await api.post(DASHBOARD_ALERTS, payload, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('DashboardService createAlert error:', error);
            throw error;
        }
    },

    /**
     * Dismisses a system alert.
     * @param {string} tenantId - The active tenant identifier.
     * @param {string|number} alertId - The target alert identifier.
     * @returns {Promise<any>}
     */
    dismissAlert: async (tenantId, alertId) => {
        try {
            const response = await api.request({
                method: 'PATCH',
                url: DISMISS_ALERT(alertId),
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('DashboardService dismissAlert error:', error);
            throw error;
        }
    },

    /**
     * Resolves a system alert.
     * @param {string} tenantId - The active tenant identifier.
     * @param {string|number} alertId - The target alert identifier.
     * @returns {Promise<any>}
     */
    resolveAlert: async (tenantId, alertId) => {
        try {
            const response = await api.request({
                method: 'PATCH',
                url: RESOLVE_ALERT(alertId),
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('DashboardService resolveAlert error:', error);
            throw error;
        }
    },

    /**
     * Fetches the user's dashboard widget configuration and visibility preferences.
     * @param {string} tenantId - The active tenant identifier.
     * @returns {Promise<any>}
     */
    fetchPreferences: async (tenantId) => {
        try {
            const response = await api.get(DASHBOARD_PREFERENCES, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('DashboardService fetchPreferences error:', error);
            throw error;
        }
    },

    /**
     * Saves the user's dashboard widget order and visibility preferences.
     * @param {string} tenantId - The active tenant identifier.
     * @param {Object} payload - The preferences payload.
     * @param {string[]} payload.widgetOrder - Ordered array of widget string IDs.
     * @param {string[]} payload.hiddenWidgets - Array of hidden widget string IDs.
     * @returns {Promise<any>}
     */
    savePreferences: async (tenantId, payload) => {
        try {
            // Persist full preferences locally so client-only widgets (revenueBreakdown, expenseBreakdown)
            // retain their custom order and hidden state across reloads
            try {
                localStorage.setItem(`dashboard_preferences_${tenantId}`, JSON.stringify(payload));
            } catch (_) {}

            // The backend API strictly validates against an allowed widget enum:
            // "occupancy"|"revenue"|"roomStatus"|"reservations"|"housekeeping"|"staffOnDuty"|"alerts"|"weeklyChart"|"nightAudit"|"activityLog"
            const ALLOWED_BACKEND_WIDGETS = [
                'occupancy',
                'revenue',
                'roomStatus',
                'reservations',
                'housekeeping',
                'staffOnDuty',
                'alerts',
                'weeklyChart',
                'nightAudit',
                'activityLog'
            ];

            const sanitizedPayload = {
                widgetOrder: Array.isArray(payload?.widgetOrder)
                    ? payload.widgetOrder.filter(id => ALLOWED_BACKEND_WIDGETS.includes(id))
                    : [],
                hiddenWidgets: Array.isArray(payload?.hiddenWidgets)
                    ? payload.hiddenWidgets.filter(id => ALLOWED_BACKEND_WIDGETS.includes(id))
                    : []
            };

            const response = await api.put(DASHBOARD_PREFERENCES, sanitizedPayload, {
                headers: { 'x-tenant-id': tenantId }
            });
            return response.data?.data || response.data || response;
        } catch (error) {
            console.error('DashboardService savePreferences error:', error);
            throw error;
        }
    },
};
